import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { FiFilter, FiDownload, FiCheck, FiX, FiEye, FiArrowLeft } from 'react-icons/fi';
import axios from 'axios';
import { WithdrawalStatusBadge, formatCurrency } from '../components/TransactionStatus';

const WithdrawalRequests = () => {
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vendors, setVendors] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  
  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalWithdrawals: 0,
    limit: 10
  });
  
  // Filter state
  const [filters, setFilters] = useState({
    vendorId: '',
    status: '',
    startDate: '',
    endDate: '',
    minAmount: '',
    maxAmount: ''
  });
  
  // Process withdrawal dialog state
  const [showDialog, setShowDialog] = useState(false);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
  const [actionType, setActionType] = useState(''); // 'approve' or 'reject'
  const [notes, setNotes] = useState('');
  const [processingAction, setProcessingAction] = useState(false);
  
  useEffect(() => {
    fetchVendors();
    fetchWithdrawals();
  }, [pagination.currentPage]);
  
  const fetchVendors = async () => {
    try {
        const token = localStorage.getItem('token');
      
      if (!token) {
        alert('Authentication token not found. Please log in again.');
        return;
      }
       const apiUrl = process.env.REACT_APP_API_URL || 'https://api.bookwie.com';
       const response = await axios.get(`${apiUrl}/api/admin/vendor/list?limit=100`,{headers:{
        'Authorization': `Bearer ${token}`
      }});
      if (response.data.success) {
        setVendors(response.data.data.vendors);
      }
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  };
  
  const fetchWithdrawals = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      // Add pagination
      params.append('page', pagination.currentPage);
      params.append('limit', pagination.limit);
      
      // Add all active filters
      Object.keys(filters).forEach(key => {
        if (filters[key]) {
          params.append(key, filters[key]);
        }
      });
      
      const token = localStorage.getItem('token');
      
      if (!token) {
        alert('Authentication token not found. Please log in again.');
        return;
      }
      const apiUrl = process.env.REACT_APP_API_URL || 'https://api.bookwie.com';
      const response = await axios.get(`${apiUrl}/api/wallet/admin/pending-withdrawals?${params.toString()}`,{headers:{
        'Authorization': `Bearer ${token}`
      }});
      
      if (response.data.success) {
        setWithdrawals(response.data.data.withdrawals);
        setPagination({
          ...pagination,
          totalPages: response.data.data.pageCount,
          totalWithdrawals: response.data.data.totalCount
        });
      }
    } catch (error) {
      console.error('Error fetching withdrawals:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const applyFilters = () => {
    setPagination(prev => ({
      ...prev,
      currentPage: 1
    }));
    fetchWithdrawals();
  };
  
  const resetFilters = () => {
    setFilters({
      vendorId: '',
      status: '',
      startDate: '',
      endDate: '',
      minAmount: '',
      maxAmount: ''
    });
    setPagination(prev => ({
      ...prev,
      currentPage: 1
    }));
    fetchWithdrawals();
  };
  
  const exportToCSV = () => {
    // Implementation for exporting to CSV
    alert('Export functionality will be implemented');
  };
  
  const openProcessDialog = (withdrawal, action) => {
    setSelectedWithdrawal(withdrawal);
    setActionType(action);
    setNotes('');
    setShowDialog(true);
  };
  
  const processWithdrawal = async () => {
    if (!selectedWithdrawal || !actionType) return;

    setProcessingAction(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://api.bookwie.com';
      const token = localStorage.getItem('token');
      const response = await axios.post(`${apiUrl}/api/wallet/admin/process-withdrawal`, {
        vendorId: selectedWithdrawal.vendorId,
        requestId: selectedWithdrawal.requestId,
        status: actionType === 'approve' ? 'approved' : 'rejected',
        notes
      }, { headers: { 'Authorization': `Bearer ${token}` } });
      
      if (response.data.success) {
        // Remove the processed withdrawal from the list or update its status
        setWithdrawals(prev => 
          prev.filter(w => w.requestId !== selectedWithdrawal.requestId)
        );
        
        setShowDialog(false);
        // You might want to show a success message here
      }
    } catch (error) {
      console.error('Error processing withdrawal:', error);
      alert(`Failed to ${actionType} withdrawal: ${error.response?.data?.message || 'Unknown error'}`);
    } finally {
      setProcessingAction(false);
    }
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'dd MMM yyyy, hh:mm a');
    } catch (e) {
      return dateString;
    }
  };
  
  const renderPagination = () => {
    if (pagination.totalPages <= 1) return null;
    
    const pageNumbers = [];
    for (let i = 1; i <= pagination.totalPages; i++) {
      pageNumbers.push(i);
    }
    
    return (
      <div className="flex justify-center mt-4">
        <div className="flex space-x-1">
          <button
            onClick={() => setPagination(prev => ({ 
              ...prev, 
              currentPage: Math.max(prev.currentPage - 1, 1) 
            }))}
            disabled={pagination.currentPage === 1}
            className={`px-3 py-1 rounded ${
              pagination.currentPage === 1 
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed' 
                : 'bg-white border border-gray-300 hover:bg-gray-50'
            }`}
          >
            Prev
          </button>
          
          {pageNumbers.map(number => (
            <button
              key={number}
              onClick={() => setPagination(prev => ({ ...prev, currentPage: number }))}
              className={`px-3 py-1 rounded ${
                pagination.currentPage === number
                  ? 'bg-blue-600 text-white'
                  : 'bg-white border border-gray-300 hover:bg-gray-50'
              }`}
            >
              {number}
            </button>
          ))}
          
          <button
            onClick={() => setPagination(prev => ({ 
              ...prev, 
              currentPage: Math.min(prev.currentPage + 1, prev.totalPages) 
            }))}
            disabled={pagination.currentPage === pagination.totalPages}
            className={`px-3 py-1 rounded ${
              pagination.currentPage === pagination.totalPages
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                : 'bg-white border border-gray-300 hover:bg-gray-50'
            }`}
          >
            Next
          </button>
        </div>
      </div>
    );
  };
  
  return (
    <div className="space-y-5">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-gray-900">Withdrawal Requests</h1>
        <p className="text-gray-600">Manage vendor withdrawal requests</p>
      </div>
      
      {/* Action Buttons */}
      <div className="flex justify-between mb-6">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <FiFilter className="mr-2" />
          {showFilters ? 'Hide Filters' : 'Show Filters'}
        </button>
        
        <button
          onClick={exportToCSV}
          className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <FiDownload className="mr-2" />
          Export CSV
        </button>
      </div>
      
      {/* Filters Section */}
      {showFilters && (
        <div className="bg-white p-4 rounded-lg shadow-sm mb-6 border border-gray-200">
          <h2 className="text-lg font-medium mb-4">Filter Withdrawal Requests</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Vendor Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Vendor
              </label>
              <select
                name="vendorId"
                value={filters.vendorId}
                onChange={handleFilterChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Vendors</option>
                {vendors.map(vendor => (
                  <option key={vendor._id} value={vendor._id}>
                    {vendor.store_name || vendor.name}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
            
            {/* Date Range Filters */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date
              </label>
              <input
                type="date"
                name="startDate"
                value={filters.startDate}
                onChange={handleFilterChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Date
              </label>
              <input
                type="date"
                name="endDate"
                value={filters.endDate}
                onChange={handleFilterChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            {/* Amount Range Filters */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Min Amount (₹)
              </label>
              <input
                type="number"
                name="minAmount"
                value={filters.minAmount}
                onChange={handleFilterChange}
                placeholder="0.00"
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Amount (₹)
              </label>
              <input
                type="number"
                name="maxAmount"
                value={filters.maxAmount}
                onChange={handleFilterChange}
                placeholder="100000.00"
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          
          <div className="mt-4 flex space-x-2">
            <button
              onClick={applyFilters}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Apply Filters
            </button>
            <button
              onClick={resetFilters}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Reset Filters
            </button>
          </div>
        </div>
      )}
      
      {/* Withdrawals Table */}
      <div className="bg-white rounded-3xl shadow-card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">Loading withdrawal requests...</span>
          </div>
        ) : withdrawals.length === 0 ? (
          <div className="text-center py-10">
            <p className="text-gray-500">No withdrawal requests found</p>
            <button
              onClick={resetFilters}
              className="mt-2 text-blue-600 hover:underline text-sm"
            >
              Reset filters
            </button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Request ID
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Store Name
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Requested On
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Bank Details
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {withdrawals.map(withdrawal => (
                    <tr key={withdrawal.requestId} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {withdrawal.requestId.substring(0, 8)}...
                        </div>
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {withdrawal.storeName || withdrawal.vendorName}
                        </div>
                        <div className="text-xs text-gray-500">
                          {withdrawal.vendorEmail}
                        </div>
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-bold text-gray-900">
                          {formatCurrency(withdrawal.amount)}
                        </div>
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatDate(withdrawal.requestedAt)}
                        </div>
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap">
                        <WithdrawalStatusBadge status="pending" />
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap">
                        {withdrawal.accountDetails ? (
                          <div className="text-xs">
                            <p><span className="font-medium">Name:</span> {withdrawal.accountDetails.accountHolderName || 'N/A'}</p>
                            <p><span className="font-medium">Acc:</span> {withdrawal.accountDetails.accountNumber}</p>
                            <p><span className="font-medium">IFSC:</span> {withdrawal.accountDetails.ifscCode}</p>
                            <p><span className="font-medium">Bank:</span> {withdrawal.accountDetails.bankName || 'N/A'}</p>
                            {withdrawal.accountDetails.branchName && (
                              <p><span className="font-medium">Branch:</span> {withdrawal.accountDetails.branchName}</p>
                            )}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-500">No bank details</span>
                        )}
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => openProcessDialog(withdrawal, 'approve')}
                            className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-green-700 bg-green-100 hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                          >
                            <FiCheck className="mr-1" size={14} />
                            Approve
                          </button>
                          <button
                            onClick={() => openProcessDialog(withdrawal, 'reject')}
                            className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                          >
                            <FiX className="mr-1" size={14} />
                            Reject
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  Showing {withdrawals.length} of {pagination.totalWithdrawals} withdrawal requests
                </div>
                {renderPagination()}
              </div>
            </div>
          </>
        )}
      </div>
      
      {/* Process Withdrawal Dialog */}
      {showDialog && (
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div className="fixed inset-0 bg-half-transparent transition-opacity" onClick={() => setShowDialog(false)}></div>
            
            {/* Dialog Panel */}
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className={`mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full ${
                    actionType === 'approve' ? 'bg-green-100' : 'bg-red-100'
                  } sm:mx-0 sm:h-10 sm:w-10`}>
                    {actionType === 'approve' ? (
                      <FiCheck className="h-6 w-6 text-green-600" />
                    ) : (
                      <FiX className="h-6 w-6 text-red-600" />
                    )}
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      {actionType === 'approve' ? 'Approve Withdrawal Request' : 'Reject Withdrawal Request'}
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        {actionType === 'approve' 
                          ? 'Are you sure you want to approve this withdrawal request? This action cannot be undone.'
                          : 'Are you sure you want to reject this withdrawal request? This action cannot be undone.'}
                      </p>
                    </div>
                    
                    {/* Withdrawal Details */}
                    {selectedWithdrawal && (
                      <div className="mt-4 bg-gray-50 p-3 rounded-md">
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div>
                            <span className="text-gray-500">Store:</span>
                            <p className="font-medium">{selectedWithdrawal.storeName || selectedWithdrawal.vendorName}</p>
                          </div>
                          <div>
                            <span className="text-gray-500">Amount:</span>
                            <p className="font-medium">{formatCurrency(selectedWithdrawal.amount)}</p>
                          </div>
                          <div className="col-span-2">
                            <span className="text-gray-500">Bank Details:</span>
                            {selectedWithdrawal.accountDetails ? (
                              <div className="text-xs mt-1">
                                <p><span className="font-medium">Acc:</span> {selectedWithdrawal.accountDetails.accountNumber}</p>
                                <p><span className="font-medium">IFSC:</span> {selectedWithdrawal.accountDetails.ifscCode}</p>
                                <p><span className="font-medium">Bank:</span> {selectedWithdrawal.accountDetails.bankName || 'N/A'}</p>
                              </div>
                            ) : (
                              <p className="font-medium">No bank details available</p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Notes */}
                    <div className="mt-4">
                      <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
                        Notes {actionType === 'reject' && '(Required for rejection)'}
                      </label>
                      <textarea
                        id="notes"
                        name="notes"
                        rows="3"
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        placeholder={actionType === 'approve' 
                          ? "Optional notes about this approval" 
                          : "Please provide a reason for rejection"}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                      ></textarea>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm ${
                    actionType === 'approve' 
                      ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500' 
                      : 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                  } ${processingAction ? 'opacity-50 cursor-not-allowed' : ''}`}
                  onClick={processWithdrawal}
                  disabled={processingAction || (actionType === 'reject' && !notes.trim())}
                >
                  {processingAction 
                    ? 'Processing...' 
                    : actionType === 'approve' ? 'Approve' : 'Reject'}
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setShowDialog(false)}
                  disabled={processingAction}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WithdrawalRequests;